import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../App';
import MatchDetail from './MatchDetail';
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

const MARKETS = [
  { key: '1x2', label: '1X2' },
  { key: 'ou25', label: 'O/U 2.5' },
  { key: 'ou15', label: 'O/U 1.5' },
  { key: 'ou35', label: 'O/U 3.5' },
  { key: 'btts', label: 'BTTS' },
  { key: 'dc', label: 'Double Chance' },
];

function formatKickoff(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleDateString('en-BW', { day: 'numeric', month: 'short' }) +
    ', ' + d.toLocaleTimeString('en-BW', { hour: '2-digit', minute: '2-digit' });
}

function OddsCell({ price, bookmaker }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <span style={{
        fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14,
        color: 'var(--accent)', background: 'rgba(0,229,176,0.08)',
        padding: '2px 6px', borderRadius: 4, display: 'block',
      }}>
        {price ?? '—'}
      </span>
      {bookmaker && (
        <span style={{ fontSize: 10, color: 'var(--text3)', display: 'block', marginTop: 2 }}>
          {BOOKMAKER_LABELS[bookmaker] || bookmaker}
        </span>
      )}
    </div>
  );
}

function getMarketOdds(item, market) {
  switch (market) {
    case '1x2':
      return {
        col1: { label: 'Home', price: item.bestHome?.price, bookmaker: item.bestHome?.bookmaker },
        col2: { label: 'Draw', price: item.bestDraw?.price, bookmaker: item.bestDraw?.bookmaker },
        col3: { label: 'Away', price: item.bestAway?.price, bookmaker: item.bestAway?.bookmaker },
      };
    case 'ou25':
      return {
        col1: { label: 'Over 2.5', price: item.bestOver25?.price, bookmaker: item.bestOver25?.bookmaker },
        col2: { label: 'Under 2.5', price: item.bestUnder25?.price, bookmaker: item.bestUnder25?.bookmaker },
        col3: null,
      };
    case 'ou15':
      return {
        col1: { label: 'Over 1.5', price: item.bestOver15?.price, bookmaker: item.bestOver15?.bookmaker },
        col2: { label: 'Under 1.5', price: item.bestUnder15?.price, bookmaker: item.bestUnder15?.bookmaker },
        col3: null,
      };
    case 'ou35':
      return {
        col1: { label: 'Over 3.5', price: item.bestOver35?.price, bookmaker: item.bestOver35?.bookmaker },
        col2: { label: 'Under 3.5', price: item.bestUnder35?.price, bookmaker: item.bestUnder35?.bookmaker },
        col3: null,
      };
    case 'btts':
      return {
        col1: { label: 'Yes', price: item.bestBttsYes?.price, bookmaker: item.bestBttsYes?.bookmaker },
        col2: { label: 'No', price: item.bestBttsNo?.price, bookmaker: item.bestBttsNo?.bookmaker },
        col3: null,
      };
      case 'dc':
      return {
        col1: { label: '1X', price: item.best1X?.price, bookmaker: item.best1X?.bookmaker },
        col2: { label: 'X2', price: item.bestX2?.price, bookmaker: item.bestX2?.bookmaker },
        col3: { label: '12', price: item.best12?.price, bookmaker: item.best12?.bookmaker },
      };
    default:
      return { col1: null, col2: null, col3: null };
  }
}

export default function BestOdds() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [league, setLeague] = useState('ALL');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [market, setMarket] = useState('1x2');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  useEffect(() => {
    apiFetch('/best-odds/leagues').then(data => setLeagues(data.leagues || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200 });
      if (league !== 'ALL') params.set('league', league);
      if (search.trim()) params.set('search', search.trim());
      const [data, sum] = await Promise.all([
        apiFetch(`/best-odds?${params}`),
        apiFetch('/best-odds/summary'),
      ]);
      setItems(data.items || []);
      setSummary(sum?.summary);
      setLastRefresh(new Date());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [status, league, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => setSearch(searchInput), 400); return () => clearTimeout(t); }, [searchInput]);
  useEffect(() => {
  const t = setInterval(() => {
    if (!document.hidden) load();
  }, 120000);
  
  const handleVisibility = () => {
    if (!document.hidden) load();
  };
  document.addEventListener('visibilitychange', handleVisibility);
  
  return () => {
    clearInterval(t);
    document.removeEventListener('visibilitychange', handleVisibility);
  };
}, [load]);

  if (selected) return <MatchDetail item={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="no-select" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Watermark />

      {/* Summary cards */}
      {summary && (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
    <div className="stat-card" style={{ '--accent-color': 'var(--accent)' }}>
      <div className="stat-label">Total Matches</div>
      <div className="stat-value">{summary.total}</div>
      <div className="stat-change up">↑ Football only</div>
    </div>
    <div className="stat-card" style={{ '--accent-color': 'var(--accent2)' }}>
      <div className="stat-label">Bookmakers</div>
      <div className="stat-value">6</div>
      <div className="stat-change neutral">→ All BW bookmakers</div>
    </div>
  </div>
)}

      {/* Market selector */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {MARKETS.map(m => (
          <button
            key={m.key}
            onClick={() => setMarket(m.key)}
            className={market === m.key ? 'btn btn-primary' : 'btn btn-outline'}
            style={{ fontSize: 12, padding: '6px 14px' }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto auto auto', gap: 10, alignItems: 'end' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 13, pointerEvents: 'none' }}>🔍</span>
            <input
              type="text" placeholder="Search team or league..."
              value={searchInput} onChange={e => setSearchInput(e.target.value)}
              style={{ width: '100%', fontSize: 16, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 10px 8px 30px', color: 'var(--text)', outline: 'none' }}
            />
            {searchInput && (
              <button onClick={() => { setSearchInput(''); setSearch(''); }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 14, padding: 0 }}>✕</button>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>League</label>
            <select value={league} onChange={e => setLeague(e.target.value)} style={{ fontSize: 13, maxWidth: isMobile ? '100%' : 160 }}>
              <option value="ALL">All Leagues</option>
              {leagues.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {lastRefresh && <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
            <button className="btn btn-outline" style={{ fontSize: 11, padding: '5px 12px', whiteSpace: 'nowrap' }} onClick={load} disabled={loading}>
              {loading ? '...' : '↺ Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      {!loading && (
        <div style={{ fontSize: 12, color: 'var(--text3)', paddingLeft: 4 }}>
          {items.length} {items.length === 1 ? 'match' : 'matches'}
          {search ? ` for "${search}"` : ''}{league !== 'ALL' ? ` in ${league}` : ''}
        </div>
      )}

      {/* Table */}
      <div className="card">
        {loading ? (
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[...Array(8)].map((_, i) => <div key={i} className="skeleton" style={{ height: 52 }} />)}
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <p>No matches found.</p>
          </div>
        ) : isMobile ? (
          <div>
            {items.map(item => {
              const isArb = item.status === 'ARB';
              const isNear = item.status === 'NEAR_ARB';
              const odds = getMarketOdds(item, market);
              return (
                <div key={item._id} onClick={() => setSelected(item)} style={{
                  padding: '12px 14px', borderBottom: '1px solid var(--border)',
                  background: isArb ? 'rgba(18,201,142,0.04)' : isNear ? 'rgba(245,166,35,0.03)' : 'transparent',
                  cursor: 'pointer',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.homeTeam} vs {item.awayTeam}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{item.league}</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: odds.col3 ? '1fr 1fr 1fr' : '1fr 1fr', gap: 4, textAlign: 'center' }}>
                    {odds.col1 && (
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{odds.col1.label}</div>
                        <OddsCell price={odds.col1.price} bookmaker={odds.col1.bookmaker} />
                      </div>
                    )}
                    {odds.col2 && (
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{odds.col2.label}</div>
                        <OddsCell price={odds.col2.price} bookmaker={odds.col2.bookmaker} />
                      </div>
                    )}
                    {odds.col3 && (
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{odds.col3.label}</div>
                        <OddsCell price={odds.col3.price} bookmaker={odds.col3.bookmaker} />
                      </div>
                    )}
                  </div>
                  {market !== 'dc' && (
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Book: {item.bookPercent?.toFixed(2)}%</span>
                    <span style={{ color: item.arbMargin > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                      {item.arbMargin > 0 ? '+' : ''}{item.arbMargin?.toFixed(2)}%
                    </span>
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Desktop table */
          <table className="data-table">
            <thead>
              <tr>
                <th>Match</th>
                <th>League</th>
                <th>Kickoff</th>
                {market === '1x2' ? (
                  <>
                    <th style={{ textAlign: 'center' }}>Home</th>
                    <th style={{ textAlign: 'center' }}>Draw</th>
                    <th style={{ textAlign: 'center' }}>Away</th>
                  </>
                ) : market === 'btts' ? (
                  <>
                    <th style={{ textAlign: 'center' }}>BTTS Yes</th>
                    <th style={{ textAlign: 'center' }}>BTTS No</th>
                  </>
                  ) : market === 'dc' ? (
                  <>
                    <th style={{ textAlign: 'center' }}>1X</th>
                    <th style={{ textAlign: 'center' }}>X2</th>
                    <th style={{ textAlign: 'center' }}>12</th>
                  </>
                ) : (
                  <>
                    <th style={{ textAlign: 'center' }}>Over {market === 'ou15' ? '1.5' : market === 'ou25' ? '2.5' : '3.5'}</th>
                    <th style={{ textAlign: 'center' }}>Under {market === 'ou15' ? '1.5' : market === 'ou25' ? '2.5' : '3.5'}</th>
                  </>
                )}
                <th style={{ textAlign: 'right' }}>Book %</th>
                <th style={{ textAlign: 'right' }}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const isArb = item.status === 'ARB';
                const isNear = item.status === 'NEAR_ARB';
                const odds = getMarketOdds(item, market);
                return (
                  <tr key={item._id} onClick={() => setSelected(item)}
                    style={{ background: isArb ? 'rgba(18,201,142,0.04)' : isNear ? 'rgba(245,166,35,0.03)' : 'transparent', cursor: 'pointer' }}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{item.homeTeam} vs {item.awayTeam}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Click to compare all bookmakers →</div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>{item.league}</td>
                    <td style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{formatKickoff(item.commenceTime)}</td>
                    {odds.col1 && <td><OddsCell price={odds.col1.price} bookmaker={odds.col1.bookmaker} /></td>}
                    {odds.col2 && <td><OddsCell price={odds.col2.price} bookmaker={odds.col2.bookmaker} /></td>}
                    {odds.col3 && <td><OddsCell price={odds.col3.price} bookmaker={odds.col3.bookmaker} /></td>}
                    {!odds.col3 && <td></td>}
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-display)', fontSize: 13 }}>{item.bookPercent?.toFixed(2)}%</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-display)', fontSize: 13, color: item.arbMargin > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                      {market !== 'dc' ? `${item.arbMargin > 0 ? '+' : ''}${item.arbMargin?.toFixed(2)}%` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}